import { Select as SelectPrimitive } from "@kobalte/core/select";
import { Check, ChevronsUpDown, Plus } from "lucide-solid";
import { type JSX, Show, splitProps } from "solid-js";
import { cn } from "@comandero/shared";

/**
 * Opción de un Select premium. `value` es la clave estable que se persiste y
 * `label` el texto mostrado (y usado por el typeahead de teclado de Kobalte).
 */
interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/**
 * CTA que se renderiza dentro del dropdown cuando el Select no tiene opciones
 * (estilo AWS: un combo vacío ofrece "Crear X" / "Ir a X" en vez de un control
 * muerto).
 *
 * Pasa `onSelect` para ejecutar una acción (abrir un diálogo de creación o
 * enfocar un input) o `href` para renderizar un enlace (navegar a la pantalla
 * que provee las opciones). `can` (default `true`) gatea la CTA por permiso —
 * cuando es `false` el dropdown vacío cae a un mensaje "sin opciones" y no se
 * muestra la afordancia de creación. Omite `emptyAction` si el origen no es
 * creable.
 */
export interface SelectEmptyAction {
  label: string;
  onSelect?: () => void;
  href?: string;
  /** Gate de permiso. Cuando es false, la CTA se oculta. Default true. */
  can?: boolean;
  /** Icono inicial de la CTA. Default: un glifo de "+". */
  icon?: JSX.Element;
  /** Línea de ayuda opcional sobre la CTA (p.ej. "Aún sin categorías"). */
  description?: string;
}

interface SelectProps {
  /** Opciones a renderizar. */
  options: SelectOption[];
  /** Valor controlado (clave). Usa `""`/undefined para "sin selección". */
  value?: string;
  /** Notifica el nuevo valor; `""` cuando se limpia la selección. */
  onChange?: (value: string) => void;
  /** Placeholder cuando no hay valor (equivale a la opción vacía nativa). */
  placeholder?: string;
  /** aria-label / nombre accesible del control. */
  "aria-label"?: string;
  /** testid del trigger (mantiene compat. con los selects nativos previos). */
  "data-testid"?: string;
  disabled?: boolean;
  class?: string;
  /** Ancho mínimo del panel; por defecto iguala al trigger. */
  contentClass?: string;
  /** Si true, el trigger ocupa todo el ancho disponible. */
  fluid?: boolean;
  /** Tamaño visual del trigger. */
  size?: "sm" | "md" | "lg";
  /**
   * Se muestra dentro del dropdown cuando `options` está vacío. Permite saltar
   * al lugar que crea opciones en vez de enfrentar un Select muerto. Ver
   * {@link SelectEmptyAction}.
   */
  emptyAction?: SelectEmptyAction;
  /** Mensaje en el dropdown vacío cuando no hay CTA (o sin permiso). */
  emptyLabel?: string;
}

const triggerSizes = {
  sm: "h-8 px-2 text-xs gap-1.5",
  md: "h-10 px-3 text-sm gap-2",
  lg: "h-12 px-4 text-base gap-2",
} as const;

/**
 * Select premium basado en `@kobalte/core` que reemplaza al `<select>` nativo.
 *
 * - Theme-safe: usa tokens del repo (accent/accent-foreground, border, popover…).
 * - Teclado + typeahead + ARIA listos de fábrica (Kobalte).
 * - Panel portalizado animado SOLO en opacidad (`.ui-select-content`), nunca en
 *   transform/scale: eso pisaría a @floating-ui y causaría pestañeo al abrir.
 *
 * Se modela como single-select sobre `string`. La selección vacía se representa
 * con `""` (o `undefined`): Kobalte usa `null` internamente, así que mapeamos
 * en los bordes para no propagar `null` al resto de la app.
 */
export function Select(props: SelectProps) {
  const [local] = splitProps(props, [
    "options",
    "value",
    "onChange",
    "placeholder",
    "aria-label",
    "data-testid",
    "disabled",
    "class",
    "contentClass",
    "fluid",
    "size",
    "emptyAction",
    "emptyLabel",
  ]);

  const size = () => local.size ?? "md";
  const isEmpty = () => local.options.length === 0;

  // Mapea la clave controlada a la opción correspondiente (o null = sin valor).
  const selected = (): SelectOption | null =>
    local.options.find((o) => o.value === local.value) ?? null;

  return (
    <SelectPrimitive<SelectOption>
      options={local.options}
      optionValue="value"
      optionTextValue="label"
      optionDisabled="disabled"
      value={selected()}
      // Single-select requerido: re-clicar la opción ya seleccionada NO debe
      // des-seleccionarla (el toggle de Kobalte emitiría null → coerción a "" →
      // campo vacío antes del submit). El valor puede cambiarse a otra opción,
      // pero no vaciarse re-clicando.
      disallowEmptySelection
      onChange={(option) => local.onChange?.(option?.value ?? "")}
      disabled={local.disabled}
      placeholder={local.placeholder}
      gutter={6}
      sameWidth
      itemComponent={(itemProps) => (
        <SelectPrimitive.Item
          item={itemProps.item}
          data-testid="select-item"
          data-value={itemProps.item.rawValue.value}
          class={cn(
            "relative flex cursor-pointer select-none items-center gap-2 rounded-md py-2 pl-3 pr-8 text-sm outline-none",
            "transition-colors duration-150",
            "data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground",
            "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
          )}
        >
          <SelectPrimitive.ItemLabel>{itemProps.item.rawValue.label}</SelectPrimitive.ItemLabel>
          <SelectPrimitive.ItemIndicator class="absolute right-2 flex items-center">
            <Check class="h-4 w-4" />
          </SelectPrimitive.ItemIndicator>
        </SelectPrimitive.Item>
      )}
    >
      <SelectPrimitive.Trigger
        data-testid={local["data-testid"]}
        aria-label={local["aria-label"]}
        class={cn(
          "inline-flex items-center justify-between rounded-md border border-input bg-background",
          "text-foreground outline-none transition-colors duration-150",
          "hover:border-accent/60 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
          "active:scale-[0.99] motion-reduce:active:scale-100",
          "disabled:cursor-not-allowed disabled:opacity-50",
          triggerSizes[size()],
          local.fluid ? "w-full" : "min-w-[8rem]",
          local.class,
        )}
      >
        <SelectPrimitive.Value<SelectOption> class="truncate text-left">
          {(state) => state.selectedOption()?.label}
        </SelectPrimitive.Value>
        <SelectPrimitive.Icon class="shrink-0 opacity-60 transition-transform duration-200 data-[expanded]:rotate-180">
          <ChevronsUpDown class="h-4 w-4" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          class={cn(
            "ui-select-content z-[250] overflow-hidden rounded-xl border border-border bg-popover p-1 text-popover-foreground shadow-2xl outline-none",
            local.contentClass,
          )}
        >
          <Show
            when={!isEmpty()}
            fallback={<SelectEmptyContent action={local.emptyAction} label={local.emptyLabel} />}
          >
            <SelectPrimitive.Listbox class="max-h-72 overflow-y-auto outline-none" />
          </Show>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive>
  );
}

/**
 * Contenido del dropdown de un Select vacío. Renderiza la CTA de `emptyAction`
 * (botón de creación o enlace de navegación) cuando está presente y permitida,
 * de lo contrario un mensaje "sin opciones" atenuado.
 *
 * La CTA es alcanzable por teclado (es un button/anchor nativo, auto-enfocable
 * dentro del panel portalizado) y hereda la entrada solo-opacidad del panel
 * (`ui-select-content`), así que respeta `prefers-reduced-motion` vía el guard
 * global como el resto de la UI. NO se añade transform (rompería el guard
 * no-flicker del panel).
 */
export function SelectEmptyContent(props: { action?: SelectEmptyAction; label?: string }) {
  const ctaClass = cn(
    "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm font-medium outline-none",
    "text-popover-foreground transition-colors cursor-pointer",
    "hover:bg-accent hover:text-accent-foreground",
    "focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
  );

  const ctaIcon = () => props.action?.icon ?? <Plus class="h-4 w-4 shrink-0 text-accent" />;

  return (
    <Show
      when={props.action && props.action.can !== false ? props.action : undefined}
      fallback={
        <div class="px-2.5 py-3 text-center text-sm text-muted-foreground">
          {/* La copia vive en el call site para que este primitivo siga
              i18n-clean; el em-dash neutro es el placeholder de último recurso
              cuando no se pasa label. */}
          {props.label ?? "—"}
        </div>
      }
    >
      {(action) => (
        <div class="space-y-1">
          <Show when={action().description}>
            <p class="px-2.5 pt-1 text-xs text-muted-foreground">{action().description}</p>
          </Show>
          <Show
            when={action().href}
            fallback={
              <button
                type="button"
                class={ctaClass}
                onClick={() => {
                  action().onSelect?.();
                }}
              >
                {ctaIcon()}
                <span class="truncate">{action().label}</span>
              </button>
            }
          >
            <a href={action().href} class={ctaClass}>
              {ctaIcon()}
              <span class="truncate">{action().label}</span>
            </a>
          </Show>
        </div>
      )}
    </Show>
  );
}

/**
 * Pequeño helper para construir `SelectOption[]` desde datos arbitrarios sin
 * repetir el `.map` en cada feature.
 */
export function toSelectOptions<T>(
  items: readonly T[] | undefined,
  getValue: (item: T) => string | null | undefined,
  getLabel: (item: T) => string | null | undefined,
): SelectOption[] {
  return (items ?? []).map((item) => ({
    value: getValue(item) ?? "",
    label: getLabel(item) ?? "",
  }));
}
